import { useEffect, useMemo, useState } from "react";
import "./OrganizationCreation.scss";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import AuiIcon from "../../components/AuiDropdown/AuiIcon";
import AuiButton from "../../components/Buttons/Buttons";
import DropDown from "../../components/DropDown/DropDown";
import Headings from "../../components/Headings/Headings";
import Modals from "../../components/Modals/Modals";
import {
  addOrganizationAdmins,
  createOrganization,
  fetchOrganizationAdmins,
} from "../../services/organization.service";
import {
  getCountry,
  getState,
  getOrganisationList,
  OrganisationListParams,
} from "../../services/training-session.service";
import { showAlert } from "../../store/alertSlice";
import { CryptoMethods } from "../../utils/crypto-methods";
import SearchableSingleSelectDropdown from "../../components/DynamicDropdowns/SearchableSingleSelectDropdown";
import { useConditionalDebouncedSearch } from "../../utils/constants/app-constants";
import { mergeSelectedWithList } from "../../components/Hooks/classDetailMultiDropdownHook";
import sanitizeHtml from 'sanitize-html';
import Input from "../../components/Input/Input";

export interface DropdownOption {
  id?: number;
  name?: string;
  code?: any;
  regionId?: number;
  regionCode?: string;
  regionName?: string;
}

export interface OrganizationForm {
  type?: DropdownOption;
  name?: string;
  address?: string;
  country?: DropdownOption;
  state?: DropdownOption;
  approvingOrg?: DropdownOption;
}

type AdminFields = "firstName" | "lastName" | "email";

export interface AdminDetails {
  id: string;
  firstName?: string;
  firstNameError?: string;
  firstNameTouched?: boolean;
  firstNameEncrypted?: string;
  lastName?: string;
  lastNameError?: string;
  lastNameTouched?: boolean;
  lastNameEncrypted?: string;
  email?: string;
  emailError?: string;
  emailTouched?: boolean;
  emailEncrypted?: string;
}

const OrganizationCreation = () => {
  const [adminCounter, setAdminCounter] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [states, setStates] = useState<DropdownOption[]>([]);
  const [regions, setRegions] = useState<DropdownOption[]>([]);
  const [organizations, setOrganizations] = useState<DropdownOption[]>([]);
  const [name, setName] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  const [organizationFormData, setOrganizationFormData] =
    useState<OrganizationForm>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [adminList, setAdminList] = useState<AdminDetails[]>([
    {
      id: `${Date.now()}-${Math.random()}`,
      firstName: "",
      firstNameError: "Required field",
      firstNameTouched: false,
      lastName: "",
      lastNameError: "Required field",
      lastNameTouched: false,
      email: "",
      emailError: "Required field",
      emailTouched: false,
    },
  ]);
  const [hasInvalidAdminFields, setHasInvalidAdminFields] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [approvingOrgAdmins, setApprovingOrgAdmins] = useState<any[]>([]);
  const [isCreateOrgLoading, setIsCreateOrgLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [clearError, setClearError] = useState(false);
  const [clearAddressError, setClearAddressError] = useState(false);

  const organizationTypeList = [
    { id: 1, name: "Certifying Organization" },
    { id: 2, name: "Sponsoring Organization" },
  ];

  function mergeOrganizations(orgs: any[], selectedOrg: any) {
    let merged = [...orgs];
    if (selectedOrg && !merged.some((org) => org.id === selectedOrg.id)) {
      merged = [selectedOrg, ...merged];
    }
    return merged.reduce((acc: typeof merged, curr) => {
      if (!acc.find((item) => item.id === curr.id)) {
        acc.push(curr);
      }
      return acc;
    }, []);
  }

  const fetchOrganizations = async (search: string) => {
    if (organizationFormData?.type?.name === "Sponsoring Organization") {
      const params: OrganisationListParams = {
        type: "APPROVING",
        pageNumber: 1,
        pageSize: 50,
        orderBy: "ASC",
        sortBy: "name",
        ...(search && { name: search }),
      };
      getOrganisationList(params)
        .then((result) => {
          const orgs = result?.data?.organisations || [];
          const validOrgs = orgs.filter(
            (org: { id: any; name: string }) => org.id && org.name?.trim()
          );
          const selectedOrg = organizationFormData?.approvingOrg;
          const mergedOrgList = mergeOrganizations(validOrgs, selectedOrg);
          setOrganizations(mergedOrgList);
        })
        .catch((error) => {
        console.error("Error fetching approving orgs:" + sanitizeHtml(error));
          setOrganizations([]);
        });
    }
  };

  const mergedCountryList = useMemo(
    () =>
      mergeSelectedWithList(countries, organizationFormData.country || null, "code"),
    [countries, organizationFormData.country]
  );

  const mergedStateList = useMemo(
    () => mergeSelectedWithList(states, organizationFormData.state || null, "code"),
    [states, organizationFormData.state]
  );

  const fetchCountries = async (search: string) => {
    setIsDataLoading(true);
    const fetchedCountries = await getCountry(search);
    setCountries(fetchedCountries);
    setIsDataLoading(false);
  };

  const fetchStates = async (regionCode: string, search: string = "") => {
    try {
      const fetchedStates = await getState(regionCode, search);
      setStates(fetchedStates);
    } catch (err) {
      console.error("Failed to fetch states:", err);
      setStates([]);
    }
  };

  const debouncedSearchCountries = useConditionalDebouncedSearch(fetchCountries, true);
  const debouncedSearchStates = useConditionalDebouncedSearch(
    fetchStates,
    !!organizationFormData?.country?.code,
    [organizationFormData?.country?.code]
  );

  const debouncedSearchApprovingOrgs = useConditionalDebouncedSearch(
  fetchOrganizations,
  organizationFormData?.type?.name === "Sponsoring Organization"
  );
  useEffect(() => {
    fetchCountries("");
  }, []);

  useEffect(() => {
    const setName = (event: any) => {
      updateForm("name", event.detail);
    };
    const setAddress = (event: any) => {
      updateForm("address", event.detail);
    };
    document
      .querySelector("aui-input#name")
      ?.addEventListener("auiInputChange", setName);
    document
      .querySelector("aui-input#address")
      ?.addEventListener("auiInputChange", setAddress);
    return () => {
      document
        .querySelector("aui-input#name")
        ?.removeEventListener("auiInputChange", setName);
      document
        .querySelector("aui-input#address")
        ?.removeEventListener("auiInputChange", setAddress);
    };
  }, []);

  const updateForm = async (field: string, value: string): Promise<void> => {
    const listMapper: Record<string, DropdownOption[]> = {
      type: organizationTypeList,
      country: countries,
      state: states,
      region: regions,
      approvingOrg: organizations,
    }
    if (field === "name") {
      setName(value);
      return;
    }
  
    if (field === "address") {
      setAddress(value);
      return;
    }

    const selectedOption = listMapper[field]?.find((item) => item.name === value);
    setOrganizationFormData((prev) => {
      const updatedData: OrganizationForm = {
        ...prev,
        [field]: selectedOption,
      };
      handleDependentFieldResetAndFetch(field, selectedOption, updatedData);
      return updatedData;
    });
  };
  
  const handleDependentFieldResetAndFetch = (
    field: string,
    selectedOption: DropdownOption | undefined,
    updatedData: OrganizationForm
  ) => {
    switch (field) {
      case "country":
        updatedData.state = undefined;
        setRegions([]);
        setStates([]);
        if (selectedOption?.code) fetchStates(selectedOption.code);
        break;
  
      case "type":
        updatedData.approvingOrg = {};
        setApprovingOrgAdmins([]);
        handleTypeChange(selectedOption);
        break;
  
      case "approvingOrg":
        setApprovingOrgAdmins([]);
        if (selectedOption?.code) getOrgAdminList(selectedOption.code);
        break;
      default:
        break;
    }
  };
  
  const handleTypeChange = (selectedOption: DropdownOption | undefined) => {
    if (selectedOption?.name === "Sponsoring Organization") {
      const orderBy = "ASC";
      const sortBy = "name";
      const params: OrganisationListParams = {
        type: "APPROVING",
        pageNumber: 1,
        pageSize: 50,
        orderBy,
        sortBy,
      };
      getOrganisationList(params)
        .then((result) => {
          const orgs = result?.data?.organisations || [];
          const validOrgs = orgs.filter((org: { id: any; name: string; }) => org.id && org.name?.trim());
          setOrganizations(validOrgs);
        })
        .catch((error) => {
          console.error("Error fetching approving orgs:", sanitizeHtml(error));
          setOrganizations([]);
        });
    } else {
      setOrganizations([]);
    }
  };

  useEffect(() => {
  }, [selectedIndex]);

  const getOrgAdminList = async (code: string) => {
    const orgInfoUsers = await fetchOrganizationAdmins(code);
    if (orgInfoUsers?.error) {
      setApprovingOrgAdmins([]);
    } else {
      setApprovingOrgAdmins(orgInfoUsers || []);
    }
  }

  useEffect(() => {
    setIsFormValid(
      !!name.trim() &&
        !!address.trim() &&
        !!organizationFormData?.type?.id &&
        !!organizationFormData?.country?.id &&
        !!organizationFormData?.state?.id &&
        !!organizationFormData?.country?.regionId &&
        (organizationFormData?.type?.id === 2
          ? !!organizationFormData?.approvingOrg?.id
          : true)
    );
  }, [name, address, organizationFormData])

  const updateAdminEntry = async (index: number, field: string, value: string) => {
    let encryptedValue = value;
    if (["firstName", "lastName", "email"].includes(field)) {
      if (value) {
        encryptedValue = await CryptoMethods.ecbEncryptData(value);
      }
    }
    const latestAdminEntry: any = {
      ...adminList[index],
      [field]: value,
      [`${field}Encrypted`]: encryptedValue,
    };
    if (!latestAdminEntry[field as AdminFields]?.trim()) {
      latestAdminEntry[`${field as AdminFields}Error`] = "Required field";
    } else if (["firstName", "lastName"].includes(field)) {
        latestAdminEntry[`${field as AdminFields}Error`] = "";
      } else if (field === "email") {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!regex.test(value)) {
          latestAdminEntry[`${field as AdminFields}Error`] = "Invalid email";
        } else {
          const isDuplicate = adminList.some(
            (admin, i) => admin.email === value && i !== index
          );
          latestAdminEntry[`${field as AdminFields}Error`] = isDuplicate
            ? "Duplicate email"
            : "";
        }
    }
      if (["firstName", "lastName", "email"].includes(field)) {
        latestAdminEntry[`${field}Touched`] = true;
      }
    const updatedList = adminList.map((admin: AdminDetails, i: number) =>
      index === i ? latestAdminEntry : admin
    );
    setAdminList(updatedList);
  };

  useEffect(() => {
    setHasInvalidAdminFields(!!adminList.find(
      (admin: AdminDetails) =>
        (admin.firstNameError ?? '') || (admin.lastNameError ?? '') || (admin.emailError ?? '')
    ))
  }, [adminList])

  const createNewAdmin = () => {
    setAdminList([
      ...adminList,
      {
        id: `admin-${adminCounter}`,
        firstName: "",
        firstNameError: "Required field",
        firstNameTouched: false,
        lastName: "",
        lastNameError: "Required field",
        lastNameTouched: false,
        email: "",
        emailError: "Required field",
        emailTouched: false,
      },
    ]);
    setAdminCounter(adminCounter + 1);
  };

  const handleAdminInputChange = (
    index: number,
    field: string,
    value: string
  ) => {
    setAdminList((prevList) =>
      prevList.map((admin, i) =>
        i === index
          ? { ...admin, [field]: value, [`${field}Touched`]: true }
          : admin
      )
    );
    updateAdminEntry(index, field, value);
  };

  const deleteAdmin = (index: number) => {
    const updatedList = adminList.filter((_, i) => i !== index);
    updatedList.forEach((admin, i) => {
      if (
        admin.email &&
        updatedList.filter((a) => a.email === admin.email).length === 1
      ) {
        admin.emailError = "";
      }
    });
    setAdminList(updatedList);
  };

  const createNewOrganization = async () => {
    if (!isFormValid || hasInvalidAdminFields || isCreateOrgLoading) {
      return;
    }
    setIsCreateOrgLoading(true);
    const payload: Record<string, any> = {
      name,
      type: organizationFormData?.type?.id === 2 ? "SPONSORING" : "APPROVING",
      contactDetails: address,
      isActive: true,
      organisationGeography: {
        isActive: true,
        countryID: organizationFormData?.country?.id,
        stateID: organizationFormData?.state?.id,
        regionID: organizationFormData?.country?.regionId,
      },
    };
    if (payload.type === "SPONSORING") {
      payload.referenceOrgID = organizationFormData?.approvingOrg?.id;
    }
    const organizationInfo = await createOrganization(payload);
    if (!organizationInfo?.error) {
      const emailMap = new Map<string, string>();
      adminList.forEach(admin => {
        if (admin.emailEncrypted && admin.email) {
          emailMap.set(admin.emailEncrypted, admin.email);
        }
      });      
      const adminPayload = {
        users: adminList.map(admin => ({
          firstName: admin.firstNameEncrypted,
          lastName: admin.lastNameEncrypted,
          email: admin.emailEncrypted,
          userName: admin.emailEncrypted,
          organisationCode: organizationInfo?.code,
        }))
      };
      const adminResponse = await addOrganizationAdmins(adminPayload);
      if (!adminResponse?.error) {
        dispatch(
          showAlert({
            message: "Organization has been created successfully!",
            type: "success",
          })
        );
        setIsCreateOrgLoading(false);
        navigate("/organization-listing");
      } else {
        const errorMessage = adminResponse?.error?.message;
        const match = errorMessage?.match(/User already exists:\s*(.*)/);
        const encryptedEmail = match?.[1];
        const originalEmail = encryptedEmail ? emailMap.get(encryptedEmail) : null;
        dispatch(
          showAlert({
            message: originalEmail
              ? `User already exists: ${originalEmail}`
              : "Failed to add organization admins.",
            type: "error",
          })
        );
        setIsCreateOrgLoading(false);
      }
    }
    setIsCreateOrgLoading(false);
  };

  return (
    <div className="container orgCreationSection my-5">
      <div className="orgBreadcrumb">
        <aui-breadcrumb
          previouspages='[
            {"title":"All Organizations","url":"/organization-listing"}]'
          currentpage="New Organization"
        ></aui-breadcrumb>
      </div>
      <div className="border-bottom">
        <Headings size="h1">New Organization</Headings>
      </div>
      <div className="orgInputSection my-4 border-bottom">
        <div className="row d-flex align-items-start">
          <div className="col-12 col-md-5 col-lg-4">
            <DropDown
              listInBtn
              dropId="OrganizationType"
              data={organizationTypeList.map((org) => org.name)}
              dropdownLabel="Organization Type"
              dropClassName="mt-2"
              placeHolder="Select Organization"
              required={true}
              errorText="Organization Type is Mandatory"
              onChange={(value) => updateForm("type", value)}
            ></DropDown>
          </div>
          <div className="col-12 col-md-7 col-lg-8 mb-2 mt-2">
            <div className="">
              <Input
                id="organizationName"
                label="Organization Name"
                value={name}
                placeholder="Enter Organization Name"
                required={true}
                errorMessage="Organization Name is Mandatory"
                showError={clearError && !name.trim()} // ✅ only show when clearError=true & value is empty
                onChange={(val) => {
                  setClearError(true); // ensure validation is triggered
                  setName(val);
                }}
              />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12 my-2">
            <Input
            id="address"
            label="Street Address"
            value={address}
            placeholder="Type address"
            type="text"
            required={true}
            errorMessage="Street Address is Mandatory"
            showError={clearAddressError && !address.trim()} // show error only when validation fails
            onChange={(val) => {
              setClearAddressError(true); 
              setAddress(val);
            }}
          />
          </div>
        </div>
        <div className="row d-flex align-items-start mb-4 pb-2">
          <div className="col-12 col-md-6 col-lg-4">
            <SearchableSingleSelectDropdown
              listInBtn
              data={mergedCountryList?.map((country) => country.name ?? "")}
              value={organizationFormData?.country?.name ?? ""}
              mainId="Country"
              dropdownLabel="Country"
              dropClassName="mt-2"
              placeHolder="Select Country"
              required={true}
              isLoading={isDataLoading}
              errorText="Country is Mandatory"
              onChange={(value) => updateForm("country", value)}
              onSearch={debouncedSearchCountries}
              dropId="country"
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4">
          <SearchableSingleSelectDropdown
              listInBtn
              data={[organizationFormData?.country?.regionName  ?? ""]}
              value={organizationFormData?.country?.regionName ?? ""}
              mainId="Region"
              dropdownLabel="Region"
              dropClassName="mt-2"
              placeHolder="Select Region"
              required={true}
              errorText="Region is Mandatory"
              isLoading={isDataLoading && !!organizationFormData?.country?.id}
              onChange={(value) => updateForm("region", value)}
              disabled={true}
              dropId="Region"
            />
          </div>
          <div className="col-12 col-md-6 col-lg-4">
          <SearchableSingleSelectDropdown
              listInBtn
              data={mergedStateList?.map((state) => state.name ?? "")}
              value={organizationFormData?.state?.name ?? ""}
              mainId="State"
              dropdownLabel="State"
              dropClassName="mt-2"
              placeHolder="Select State"
              required={true}
              errorText="State is Mandatory"
              isLoading={isDataLoading && !!organizationFormData?.country?.id}
              onChange={(value) => updateForm("state", value)}
              onSearch={debouncedSearchStates}
              disabled={!organizationFormData?.country?.id}
              dropId="state"
            />
          </div>
        </div>
      </div>
      <div className="adminTableSection p-3">
        <Headings
          size="h2"
          defaultClass="h8"
          className={`blue mb-2`}
          id="studentsOrgTable"
        >
          <AuiIcon
            iconClass="avataroutline"
            svgwidth="20"
            svgheight="20"
          ></AuiIcon>
          <span className="ml-1 tableTitle">Admin ({adminList.length})</span>
        </Headings>
        <div className="mt-2 mt-lg-4">
          <aui-table
            type="default"
            headerbackground={false}
            headerborder={false}
          >
            <table>
              <thead>
                <tr>
                  <th scope="col" className="p-lg-0">
                    First Name <span className="requiredLabel" aria-hidden="true">*</span>
                  </th>
                  <th scope="col" className="p-lg-0">
                    Last Name <span className="requiredLabel" aria-hidden="true">*</span>
                  </th>
                  <th scope="col" className="p-lg-0">
                    Email <span className="requiredLabel" aria-hidden="true">*</span>
                  </th>
                  <th scope="col" className="p-lg-0"></th>
                </tr>
              </thead>
              <tbody>
                {adminList.map((admin: AdminDetails, index: number) => (
                  <tr key={admin.id || `admin-${index}`}>
                    <td className="tdLabelRequired px-0 py-sm-0">
                      <div className="td-label d-lg-none">First Name</div>
                      <div className="aui-td pr-3">
                        <label
                          htmlFor={`first-name-${index}`}
                          className="sr-only"
                          id={`first-name-label-${index}`}
                        >
                          First name required, 
                        </label>
                        <input
                          id={`first-name-${index}`}
                          type="text"
                          placeholder="Enter first name"
                          value={admin.firstName}
                          aria-labelledby={`first-name-label-${index}`}
                          aria-describedby={admin.firstNameError && admin.firstNameTouched ? `first-name-error-${index}` : undefined}
                          aria-invalid={!!(admin.firstNameError && admin.firstNameTouched)}
                          onChange={(event: any) =>
                            handleAdminInputChange(
                              index,
                              "firstName",
                              event.target.value
                            )
                          }
                        />
                        {admin?.firstNameError && admin?.firstNameTouched && (
                          <div className="text-danger pt-2" id={`first-name-error-${index}`}>
                            {admin.firstNameError === "Required field"
                              ? "First name is required"
                              : admin.firstNameError}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="tdLabelRequired px-0 py-sm-0">
                      <div className="td-label d-lg-none">Last Name</div>
                      <div className="aui-td pr-3">
                        <label
                          htmlFor={`last-name-${index}`}
                          className="sr-only"
                          id={`last-name-label-${index}`}
                        >
                          Last name required,
                        </label>
                        <input
                         id={`last-name-${index}`}
                          type="text"
                          placeholder="Enter last name"
                          value={admin.lastName}
                          aria-labelledby={`last-name-label-${index}`}
                          aria-describedby={admin.lastNameError && admin.lastNameTouched ? `last-name-error-${index}` : undefined}
                          aria-invalid={!!(admin.lastNameError && admin.lastNameTouched)}
                          onChange={(event: any) =>
                            handleAdminInputChange(
                              index,
                              "lastName",
                              event.target.value
                            )
                          }
                        />
                        {admin?.lastNameError && admin?.lastNameTouched && (
                          <div className="text-danger pt-2" id={`last-name-error-${index}`}>
                            {admin.lastNameError === "Required field"
                              ? "Last name is required"
                              : admin.lastNameError}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="tdLabelRequired px-0 py-sm-0">
                      <div className="td-label d-lg-none">Email</div>
                      <div className="aui-td pr-3">
                        <label
                          htmlFor={`email-${index}`}
                          className="sr-only"
                          id={`email-label-${index}`}
                        >
                          Email required,
                        </label>{" "}
                        <input
                          id={`email-${index}`}
                          type="text"
                          placeholder="Enter admin's email"
                          value={admin.email}
                          aria-labelledby={`email-label-${index}`}
                          aria-describedby={admin.emailError && admin.emailTouched ? `email-error-${index}` : undefined}
                          aria-invalid={!!(admin.emailError && admin.emailTouched)}
                          onChange={(event: any) =>
                            handleAdminInputChange(index, "email", event.target.value)
                          }
                        />
                        {admin?.emailError &&
                          admin?.emailTouched &&
                          (() => {
                            let errorMessage = admin.emailError;

                            if (admin.emailError === "Required field") {
                              errorMessage = "Email is required";
                            } else if (admin.emailError === "Invalid email") {
                              errorMessage =
                                "Please enter a valid email address in the format user@example.com.";
                            } else if (admin.emailError === "Duplicate email") {
                              errorMessage =
                                "This email address is already used for another admin. Please enter a unique email.";
                            }

                            return (
                              <div
                                className="text-danger pt-2"
                                id={`email-error-${index}`}
                              >
                                {errorMessage}
                              </div>
                            );
                          })()}
                      </div>
                    </td>
                    <td data-title="Delete" className="p-0 adminDelete">
                      <div
                        className={`aui-td ${
                          adminList.length === 1 ? "adminDetailsDelete" : ""
                        }`}
                      >
                        <Modals
                          className="btn"
                          modalsize="auimodal-sm"
                          headingAlign="center"
                          modalTitle="Delete Admin Details"
                          buttonTitle="Delete"
                          modalid={`DeleteOrg-${index}`}
                          variant="read-more"
                          iconProps={{
                            icon: "trash",
                            svgclass: "aui-icon-trash",
                            svgwidth: "20",
                            svgheight: "20",
                            pathclass: "aui-path-trash",
                            ariaLabel: "Delete",
                          }}
                          action={() => deleteAdmin(index)}
                          disableActionButton={adminList.length === 1}
                        >
                          <p className="text-center font-400 h8">
                            Are you sure you want to delete?
                          </p>
                        </Modals>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </aui-table>
          <button
            className="btn addMoreBtn font-500 mt-4"
            onClick={createNewAdmin}
          >
            <aui-icon icon="plus" svgwidth="15" svgheight="15"></aui-icon> Add
            More{" "}
          </button>
        </div>
      </div>
      {organizationFormData?.type?.id === 2 && (
        <div className="border-bottom">
          <div className="row align-items-end sponsorSection mt-4">
            <div className="col-12 col-md-6 col-lg-4 mb-4">
              <SearchableSingleSelectDropdown
                listInBtn
                data={organizations.map((org) => org?.name ?? "")}
                value={organizationFormData?.approvingOrg?.name ?? ""}
                mainId="CertifyingOrg"
                dropdownLabel="Certifying Organization"
                dropClassName="mt-2"
                placeHolder="Select Organization"
                required={true}
                errorText="Certifying Organization is Mandatory"
                isLoading={organizations.length === 0}
                setSelectedIndex={setSelectedIndex}
                onChange={(value) => updateForm("approvingOrg", value)}
                onSearch={debouncedSearchApprovingOrgs}
                dropId="Certifying Organization"
              />
            </div>
            <div className="col-12 mt-2 mb-4">
              {(approvingOrgAdmins || []).length ? (
                <aui-table
                  type="default"
                  headerbackground={true}
                  headerborder={true}
                >
                  <table>
                    <thead>
                      <tr>
                        <th scope="col" className="adminTabCount">
                          <div className="aui-th">No</div>
                        </th>
                        <th scope="col">
                          <div className="aui-th">Admin Name</div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(approvingOrgAdmins || []).map(
                        (admin: any, index: number) => (
                          <tr key={`approving-admin-${index + 1}`}>
                            <td data-title="No">
                              <div className="aui-td">{index + 1}</div>
                            </td>
                            <td data-title="Admin Name">
                              <div className="aui-td">
                                {admin?.firstName} {admin?.lastName}
                              </div>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </aui-table>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <div className="row my-4">
        <div className=" col-12 d-flex justify-content-end">
          <Link
            to="/organization-listing"
            className="btn btn-secondary btn-md btn-round"
          >
            Cancel
          </Link>
          <AuiButton
            variant="primary"
            buttontitle="Create Organization"
            buttonid="createlOrg"
            buttonclass="px-4 ml-3 font-500"
            onClick={createNewOrganization}
            disabled={!isFormValid || hasInvalidAdminFields}
            loading={isCreateOrgLoading}
          ></AuiButton>
        </div>
      </div>
    </div>
  );
};
export default OrganizationCreation;
